/**
 * InvestorDashboardPreview
 *
 * Interactive sample dashboard rendered inside a large modal. Used on
 * /pricing and /for-investors so prospects can explore what each tier
 * looks like without signing up.
 *
 * Tracking: emits PostHog events on open, close (with dwell-time), and
 * any CTA click. See `/app/frontend/src/lib/analytics.js`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
    ArrowUpRight,
    CheckCircle,
    ShieldCheck,
    X,
    Layers,
    BarChart3,
    Activity,
    Crown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { createCheckout } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { TIER_META, TIER_DATA, LOCKED_TEASERS, formatUsd } from "./sampleData";
import {
    SectionTitle,
    StatTile,
    PropertyCard,
    PropertyComparisonTable,
    IssueCategoryList,
    RiskDistributionChart,
    NegotiationOppList,
    ActivityFeed,
    RiskHeatmap,
    PropertyRankings,
    CapitalPlanList,
    LockedFeaturePreviewCard,
} from "./PreviewWidgets";

const TIER_TITLES = {
    lite: "Sample Investor Lite Dashboard",
    pro: "Sample Investor Pro Dashboard",
    plus: "Sample Investor Plus Dashboard",
};

function HeaderBadge({ tier }) {
    if (tier === "pro") {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl-primary text-bl-surface text-[10px] tracking-[0.16em] uppercase font-medium"
                data-testid="preview-header-badge-pro"
            >
                Most Popular
            </span>
        );
    }
    if (tier === "plus") {
        return (
            <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-bl text-bl-surface text-[10px] tracking-[0.16em] uppercase font-medium"
                data-testid="preview-header-badge-plus"
            >
                <Crown className="w-3 h-3" /> Built for Serious Investors
            </span>
        );
    }
    return null;
}

function TopBar({ tier, meta, onClose }) {
    return (
        <div
            className="sticky top-0 z-10 px-4 sm:px-7 py-3.5 border-b border-bl bg-bl-surface/95 backdrop-blur flex items-center justify-between gap-3"
            data-testid="preview-top-bar"
        >
            <div className="min-w-0 flex items-center gap-3">
                <div
                    className="w-9 h-9 rounded-lg bg-bl-primary text-bl-surface flex items-center justify-center font-display font-semibold text-sm shrink-0"
                    aria-hidden
                >
                    BL
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                        {meta.tagline}
                    </div>
                    <div className="font-display font-semibold text-bl text-sm sm:text-base truncate">
                        {meta.name} · Sample Dashboard
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <HeaderBadge tier={tier} />
                <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 rounded-full hover:bg-bl-bg flex items-center justify-center text-bl-muted hover:text-bl transition-colors"
                    aria-label="Close preview"
                    data-testid="preview-close-btn"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function PreviewBanner({ tier }) {
    return (
        <div
            className="px-4 sm:px-7 py-2.5 bg-bl-teal-tint border-b border-bl text-[11px] sm:text-xs text-bl-primary font-medium tracking-wide flex items-center justify-center gap-2"
            data-testid="preview-demo-banner"
        >
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
            Sample data only — no signup or payment required. {TIER_TITLES[tier]}.
        </div>
    );
}

function PortfolioOverviewRow({ tier, meta, properties }) {
    const totalHighExposure = properties.reduce(
        (s, p) => s + (p.repair_exposure_high || 0),
        0,
    );
    const avgRisk = Math.round(
        properties.reduce((s, p) => s + (p.risk_score || 0), 0) /
            Math.max(1, properties.length),
    );
    const highPriorityIssues = properties.reduce(
        (s, p) => s + (p.high_priority_issues || 0),
        0,
    );
    return (
        <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
            data-testid="preview-overview-row"
        >
            <StatTile
                label="Properties analyzed"
                value={meta.properties_analyzed}
                sublabel={tier === "lite" ? "across this month" : "across pipeline"}
                accent="primary"
            />
            <StatTile
                label="Reports remaining"
                value={meta.reports_remaining.split(" of ")[0]}
                sublabel={meta.reports_remaining}
            />
            <StatTile
                label="Avg. risk score"
                value={avgRisk}
                sublabel="0 (clean) – 100 (severe)"
            />
            <StatTile
                label="Total exposure ceiling"
                value={formatUsd(totalHighExposure)}
                sublabel={`${highPriorityIssues} high-priority items`}
            />
        </div>
    );
}

function CtaFooter({ tier, meta, onUpgrade, onCompare, onCompareTiers, loading }) {
    // "Compare with…" defaults: lite → vs Pro, pro → vs Plus, plus → vs Pro.
    const compareCombo =
        tier === "lite" ? "lite-vs-pro" : tier === "pro" ? "pro-vs-plus" : "pro-vs-plus";
    const compareLabel =
        tier === "lite"
            ? "Compare with Investor Pro"
            : tier === "pro"
              ? "Compare with Investor Plus"
              : "Compare with Investor Pro";
    return (
        <div
            className="sticky bottom-0 z-10 px-4 sm:px-7 py-4 border-t border-bl bg-bl-surface/95 backdrop-blur flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between"
            data-testid="preview-cta-footer"
        >
            <div className="min-w-0">
                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                    {meta.tagline}
                </div>
                <div className="font-display font-semibold text-bl text-sm sm:text-base">
                    {meta.name}{" "}
                    <span className="text-bl-muted font-mono-bl text-xs">
                        {meta.price}
                        {meta.per}
                    </span>
                </div>
                {onCompareTiers && (
                    <button
                        type="button"
                        onClick={() => onCompareTiers(compareCombo)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-bl-primary hover:underline"
                        data-testid="preview-compare-link"
                    >
                        {compareLabel} →
                    </button>
                )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                    type="button"
                    onClick={() => onUpgrade(meta.id)}
                    disabled={loading}
                    className="bl-btn-primary w-full sm:w-auto"
                    data-testid={`preview-cta-primary-${tier}`}
                >
                    {loading ? "Redirecting…" : meta.primary_cta_label}
                    <ArrowUpRight className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={onCompare}
                    className="bl-btn-secondary w-full sm:w-auto"
                    data-testid={`preview-cta-secondary-${tier}`}
                >
                    {meta.secondary_cta_label}
                </button>
            </div>
        </div>
    );
}

export default function InvestorDashboardPreview({
    tier,
    open,
    onOpenChange,
    source,
    onCompareTiers,
}) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const openedAtRef = useRef(null);

    const meta = TIER_META[tier];
    const data = TIER_DATA[tier];
    const teasers = LOCKED_TEASERS[tier] || [];
    const visibleProperties = useMemo(() => {
        // Cap rendered cards for performance; comparison table covers the rest.
        if (tier === "lite") return data.properties;
        return data.properties.slice(0, 6);
    }, [tier, data.properties]);

    // Emit open / close tracking and capture dwell-time.
    useEffect(() => {
        if (!open) return undefined;
        openedAtRef.current = Date.now();
        track("investor_dashboard_preview_opened", { tier, source });
        track(`investor_${tier}_preview_opened`, { source });
        return () => {
            const dwellMs = Date.now() - (openedAtRef.current || Date.now());
            track("investor_dashboard_preview_closed", {
                tier,
                source,
                dwell_ms: dwellMs,
                dwell_seconds: Math.round(dwellMs / 1000),
            });
        };
    }, [open, tier, source]);

    const handleUpgrade = async (planId) => {
        track("investor_preview_cta_clicked", {
            tier,
            source,
            cta_target: planId,
            kind: "checkout",
        });
        try {
            if (!user) {
                navigate(`/auth/login?next=/pricing#investor-plans`);
                onOpenChange(false);
                return;
            }
            setLoading(true);
            const r = await createCheckout(planId);
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
            setLoading(false);
        }
    };

    const handleCompare = () => {
        track("investor_preview_cta_clicked", {
            tier,
            source,
            cta_target: "compare_plans",
            kind: "compare",
        });
        onOpenChange(false);
        navigate("/pricing#investor-plans");
    };

    const handleLockedUpgrade = (planId) => {
        track("investor_preview_cta_clicked", {
            tier,
            source,
            cta_target: planId,
            kind: "locked_upgrade",
        });
        handleUpgrade(planId);
    };

    const handleCompareTiers = (combo) => {
        track("investor_preview_cta_clicked", {
            tier,
            source,
            cta_target: combo,
            kind: "compare_tiers",
        });
        // Close the current preview and let the parent open the compare modal.
        onOpenChange(false);
        if (onCompareTiers) {
            // Slight delay so the close animation completes cleanly first.
            setTimeout(() => onCompareTiers(combo), 120);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[1100px] w-[96vw] sm:w-[94vw] h-[92vh] sm:h-[88vh] p-0 gap-0 bg-bl-surface border-bl rounded-xl overflow-hidden flex flex-col"
                data-testid={`investor-preview-dialog-${tier}`}
            >
                <VisuallyHidden.Root>
                    <DialogTitle>{`Sample ${meta.name} Dashboard`}</DialogTitle>
                    <DialogDescription>
                        Interactive sample dashboard for the {meta.name} tier
                        with realistic demo data. No signup required.
                    </DialogDescription>
                </VisuallyHidden.Root>
                <TopBar tier={tier} meta={meta} onClose={() => onOpenChange(false)} />
                <PreviewBanner tier={tier} />

                <div
                    className="flex-1 overflow-y-auto px-4 sm:px-7 py-5 sm:py-7 space-y-7 bl-paper"
                    data-testid={`preview-body-${tier}`}
                >
                    {/* Welcome header inside dashboard */}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="bl-eyebrow">Portfolio overview</div>
                            <h2 className="mt-1.5 font-display font-semibold text-xl sm:text-2xl text-bl">
                                Welcome back, Alex
                            </h2>
                            <p className="text-bl-muted text-sm mt-1">
                                {meta.positioning}.
                            </p>
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bl-teal-tint text-bl-primary text-[11px] tracking-[0.14em] uppercase font-medium">
                            <Layers className="w-3.5 h-3.5" />
                            {meta.name}
                        </div>
                    </div>

                    <PortfolioOverviewRow tier={tier} meta={meta} properties={data.properties} />

                    {/* Recent reports / property cards */}
                    <div>
                        <SectionTitle
                            eyebrow="Recent reports"
                            title={
                                tier === "lite"
                                    ? "Your latest standard reports"
                                    : "Latest premium reports"
                            }
                            hint={
                                tier === "lite"
                                    ? "Each report includes prioritized findings, ZIP-localized repair exposure, and shareable PDFs."
                                    : "Premium Closing Strategy is included on every report at this tier."
                            }
                            testid="preview-recent-reports-title"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {visibleProperties.map((p) => (
                                <PropertyCard
                                    key={p.id}
                                    property={p}
                                    isPremium={tier !== "lite"}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Pro+ comparison table */}
                    {tier !== "lite" && (
                        <div>
                            <SectionTitle
                                eyebrow="Deal comparison"
                                title="Side-by-side across your pipeline"
                                hint="Filter, sort, and search every report instantly — built from your stored data."
                            />
                            <PropertyComparisonTable
                                rows={data.properties.slice(0, tier === "pro" ? 8 : 12)}
                                withLeverage={true}
                            />
                        </div>
                    )}

                    {/* Risk + issues row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                        <IssueCategoryList categories={data.issue_categories} />
                        {data.risk_distribution ? (
                            <RiskDistributionChart data={data.risk_distribution} />
                        ) : (
                            <div className="bl-card p-4 sm:p-5">
                                <SectionTitle
                                    eyebrow="Risk summary"
                                    title="Across your analyzed properties"
                                    hint="Pro and Plus unlock full risk distribution histograms across your portfolio."
                                />
                                <div className="space-y-2.5 mt-1">
                                    {[
                                        ["Elevated", 1, "bg-[#C85B3A]"],
                                        ["Moderate", 1, "bg-[#D4A62A]"],
                                        ["Low", 1, "bg-bl-primary"],
                                    ].map(([band, count, tone]) => (
                                        <div
                                            key={band}
                                            className="flex items-center gap-3 text-sm"
                                        >
                                            <span
                                                className={`w-2 h-2 rounded-full ${tone}`}
                                            />
                                            <span className="text-bl flex-1">
                                                {band} risk
                                            </span>
                                            <span className="font-mono-bl text-bl-muted">
                                                {count} property
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Negotiation (Pro+) */}
                    {data.negotiation_opps && (
                        <NegotiationOppList opps={data.negotiation_opps} />
                    )}

                    {/* Plus-only advanced row */}
                    {tier === "plus" && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                            <RiskHeatmap heatmap={data.heatmap} />
                            <PropertyRankings rankings={data.rankings} />
                        </div>
                    )}
                    {tier === "plus" && <CapitalPlanList plan={data.capital_plan} />}

                    {/* Activity feed */}
                    <ActivityFeed items={data.activity} />

                    {/* Locked higher-tier teasers */}
                    {teasers.length > 0 && (
                        <div>
                            <SectionTitle
                                eyebrow="Unlock more with a higher tier"
                                title="Features available with an upgrade"
                                hint="These appear here for preview only — they activate when you upgrade."
                                testid="preview-locked-title"
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {teasers.map((t) => (
                                    <LockedFeaturePreviewCard
                                        key={t.title}
                                        teaser={t}
                                        onUpgrade={handleLockedUpgrade}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Trust footer */}
                    <div
                        className="bl-card p-4 sm:p-5 bg-bl-teal-tint/40 border-bl-primary/20"
                        data-testid="preview-trust-block"
                    >
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-bl">
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4 text-bl-primary" />
                                Cancel or switch tiers anytime
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4 text-bl-primary" />
                                Secure checkout by Stripe
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircle className="w-4 h-4 text-bl-primary" />
                                Your inspection PDFs stay private
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <BarChart3 className="w-4 h-4 text-bl-primary" />
                                Sample data only — no signup needed to explore
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <Activity className="w-4 h-4 text-bl-primary" />
                                Real reports populate this view in minutes
                            </span>
                        </div>
                    </div>
                </div>

                <CtaFooter
                    tier={tier}
                    meta={meta}
                    onUpgrade={handleUpgrade}
                    onCompare={handleCompare}
                    onCompareTiers={onCompareTiers ? handleCompareTiers : null}
                    loading={loading}
                />
            </DialogContent>
        </Dialog>
    );
}
